import { getAnonymousId, getBrowserSessionId } from "@/analytics/identity";
import {
  discoverClient,
  type DiscoverAnalyticsEventInput,
  type DiscoveryAttribution,
} from "../discoverClient";

export function discoveryAttribution(): DiscoveryAttribution {
  const query = new URLSearchParams(window.location.search);
  return {
    anonymous_id: getAnonymousId(),
    visit_id: getBrowserSessionId(),
    source: query.get("q")
      ? "search"
      : query.get("view") === "my-apps"
        ? "library"
        : query.get("view") === "discover"
          ? "discover"
          : "direct",
  };
}

/** Card displays may wait this long for company before their one request. */
const FLUSH_DELAY_MS = 5_000;
/** Send early rather than growing one batch without bound. */
const MAX_BATCH = 20;
/** Device-local control; "off" disables every Discover measurement. */
export const DISCOVER_MEASUREMENT_KEY = "ato.discover.measurement.v1";

type StoredEvent = { id: string; complete?: boolean };

export type DiscoverMeasurementView =
  | "all"
  | "popular"
  | "new"
  | "for_you"
  | "category";

export type DiscoverMeasurementContext = {
  /** 1-based position in the rendered feed; impressions only. */
  position?: number;
  /** Which list produced the click/impression; impressions only. */
  view?: DiscoverMeasurementView | string;
  /** The impression this detail/Open belongs to. */
  exposureId?: string;
};

type QueuedEvent = {
  key: string;
  stored: { id: string };
  input: DiscoverAnalyticsEventInput;
};

const pending = new Map<string, Promise<void>>();
const completed = new Set<string>();
const queued = new Map<string, QueuedEvent>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pagehideBound = false;

function readStored(key: string): StoredEvent | null {
  try {
    const value = JSON.parse(
      sessionStorage.getItem(key) ?? "null",
    ) as StoredEvent | null;
    return value && typeof value.id === "string" ? value : null;
  } catch {
    return null;
  }
}

function writeStored(key: string, value: StoredEvent): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Memory fallback. */
  }
}

/** The tab is going away; give the last impressions one best-effort request. */
function bindPagehide(): void {
  if (pagehideBound || typeof window === "undefined") return;
  pagehideBound = true;
  window.addEventListener("pagehide", () => {
    void flushDiscoverMeasurements({ keepalive: true });
  });
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushDiscoverMeasurements();
  }, FLUSH_DELAY_MS);
}

function clearFlushTimer(): void {
  if (flushTimer === null) return;
  clearTimeout(flushTimer);
  flushTimer = null;
}

/**
 * Send every queued impression in ONE request.
 *
 * A failed batch is dropped rather than retried in a loop: the queue is
 * already empty, nothing is marked complete, and the next render or visit
 * re-queues the event with the id it kept in sessionStorage (the server
 * deduplicates on it). Never throws — analytics must not break the page.
 */
export async function flushDiscoverMeasurements(
  options: { keepalive?: boolean } = {},
): Promise<void> {
  clearFlushTimer();
  if (queued.size === 0) return;
  const batch = [...queued.values()];
  queued.clear();
  const events = batch.map((event) => event.input);
  try {
    if (options.keepalive)
      await discoverClient.analyticsEvents(events, { keepalive: true });
    else await discoverClient.analyticsEvents(events);
    for (const { key, stored } of batch) {
      completed.add(key);
      writeStored(key, { ...stored, complete: true });
    }
  } catch {
    /* Dropped; a later render or visit retries with the same event id. */
  }
}

function measurementKey(
  visitId: string,
  appId: string,
  version: string,
  kind: "impression" | "detail",
): string {
  return `ato.discover-measure:${visitId}:${appId}:${version}:${kind}`;
}

/** Measurement is a device-local decision; an unreadable store keeps it on. */
export function discoverMeasurementEnabled(): boolean {
  try {
    return localStorage.getItem(DISCOVER_MEASUREMENT_KEY) !== "off";
  } catch {
    return true;
  }
}

/** Settings writes only the OFF switch; the default stays "unset = measured". */
export function setDiscoverMeasurementEnabled(enabled: boolean): void {
  try {
    if (enabled) localStorage.removeItem(DISCOVER_MEASUREMENT_KEY);
    else localStorage.setItem(DISCOVER_MEASUREMENT_KEY, "off");
  } catch {
    /* Storage can be unavailable; this page still stops sending. */
  }
}

/**
 * The exposure id this visit already kept for the card, so a detail view or
 * Open can join back to the impression it came from. Undefined when the card
 * was never measured (or the stored value is unreadable).
 */
export function readExposureId(appId: string, version: string): string | undefined {
  try {
    return readStored(
      measurementKey(getBrowserSessionId(), appId, version, "impression"),
    )?.id;
  } catch {
    return undefined;
  }
}

/** One observation per version/visit/kind. Retries keep the same event id. */
export function measureDiscover(
  appId: string,
  version: string,
  kind: "impression" | "detail",
  context: DiscoverMeasurementContext = {},
): Promise<void> {
  if (!discoverMeasurementEnabled()) return Promise.resolve();
  const attribution = discoveryAttribution();
  const key = measurementKey(attribution.visit_id, appId, version, kind);
  if (completed.has(key) || queued.has(key)) return Promise.resolve();
  if (pending.has(key)) return pending.get(key)!;
  const stored = readStored(key);
  if (stored?.complete) {
    completed.add(key);
    return Promise.resolve();
  }
  const event = { id: stored?.id ?? crypto.randomUUID() };
  writeStored(key, event);
  if (kind === "impression") {
    // A card display is batchable: resolving here means "queued", not "sent",
    // so rendering never waits on the analytics network.
    queued.set(key, {
      key,
      stored: event,
      input: {
        event_id: event.id,
        discover_app_id: appId,
        kind,
        published_version: version,
        attribution,
        ...(context.position === undefined ? {} : { position: context.position }),
        ...(context.view === undefined ? {} : { view: context.view }),
        ...(context.exposureId === undefined
          ? {}
          : { exposure_id: context.exposureId }),
      },
    });
    bindPagehide();
    if (queued.size >= MAX_BATCH) void flushDiscoverMeasurements();
    else scheduleFlush();
    return Promise.resolve();
  }
  // A detail view stays immediate and awaited: the creator conversion metric
  // joins this event's server-side `occurred_at` against the usage-session
  // `created_at`, so delaying the insert would undercount conversions.
  const work = (async () => {
    await discoverClient.analyticsEvent(appId, {
      event_id: event.id,
      kind,
      published_version: version,
      attribution,
      ...(context.exposureId === undefined
        ? {}
        : { exposure_id: context.exposureId }),
    });
    completed.add(key);
    writeStored(key, { ...event, complete: true });
  })()
    .catch(() => {
      /* Analytics failure must not block viewing or launching an app. */
    })
    .finally(() => pending.delete(key));
  pending.set(key, work);
  return work;
}
